import React from "react";
import { observer, inject } from "mobx-react";
import { observable } from "mobx";
import { AppStore } from "~/stores/AppStore";
import { awesomeIcon } from "../../utils/helpers";
import { Controller } from "./Controller";

@inject("store")
@observer
export class Notification extends React.Component<{
  store?: AppStore;
  index: number;
  data: any;
  controller: Controller;
  totHeight: number;
}> {
  @observable private destroyAnimation = false;
  @observable private hovered = false;

  private animate = true;
  private removeClassTimeout = null;
  private removeTimeout = null;
  private duration = 5000; // Duration before the notification gets discarded
  private cardRef = null;

  constructor(props) {
    super(props);
    this.selfDestruct();
    this.props.store.notificationsPopover[this.props.index].selfDestruct = (offset, instant) =>
      this.selfDestruct(offset, instant);
  }

  public componentDidMount() {
    this.props.store.notificationsPopover[this.props.index].height = this.cardRef.clientHeight;

    const el = document.getElementById(`card-${this.props.index}`);

    // Add listeners to detect when the mouse is hovered on and off
    if (el) {
      el.addEventListener("mouseenter", () => {
        this.props.controller.isExpanded = true;
      });

      el.addEventListener("mouseleave", () => {
        this.props.controller.triggerCollapse();
      });
    }

    // Remove animation class to trigger animated message on creation only
    setTimeout(() => {
      this.animate = false;
    }, 200);
  }

  // Initiate when to self destruct
  private selfDestruct = (offset = 0, instant = false) => {
    this.clearTimeouts();

    if (!instant) {
      this.addRemoveClass(offset);
      this.removeNotification(offset);
    }
  };

  private addRemoveClass = (offset) => {
    let timer = offset ? this.duration + offset * 100 : this.duration;

    // Initiate animation sequence
    this.removeClassTimeout = setTimeout(() => {
      if (!this.hovered && !this.props.controller.isExpanded) {
        this.destroyAnimation = true;
      }

      // Clear the timeout so it will not clear newly added messages if the user closed notification manually
      clearTimeout(this.removeClassTimeout);
    }, timer);
  };

  private removeNotification = (offset) => {
    let timer = offset ? this.duration + 200 + offset * 100 : this.duration + 200;

    this.removeTimeout = setTimeout(() => {
      if (!this.hovered && !this.props.controller.isExpanded) {
        this.props.store.notificationsPopover.splice(this.props.index, 1);
      }

      // Clear the timeout so it will not clear newly added messages if the user closed notification manually
      clearTimeout(this.removeTimeout);
    }, timer);
  };

  private handleClose = () => {
    this.props.store.notificationsPopover.splice(this.props.index, 1);

    // If the last card is closed, we will not be able to detect the hover off, so we automatically collapse and trigger the clearing
    if (this.props.index === this.props.store.notificationsPopover.length) {
      this.props.controller.triggerCollapse();
    }

    // Clear the timeout so it will not clear newly added messages
    this.clearTimeouts();
  };

  private clearTimeouts = () => {
    clearTimeout(this.removeClassTimeout);
    clearTimeout(this.removeTimeout);
  };

  private getIcon = (type) => {
    switch (type) {
      case "success":
        return <div style={{ color: "#66c966" }}>{awesomeIcon("check-circle")}</div>;

      case "error":
        return <div style={{ color: "#f97d7e" }}>{awesomeIcon("times-circle")}</div>;

      case "warning":
        return <div style={{ color: "#f7c86a" }}>{awesomeIcon("exclamation-circle")}</div>;

      case "info":
        return <div style={{ color: "#72acfd" }}>{awesomeIcon("info-circle")}</div>;

      default:
        return null;
    }
  };

  public render() {
    const { index, data, totHeight } = this.props;

    let desc = data.description;
    let className = this.props.controller.isFolded ? " folded" : " expanded";
    const style = this.props.controller.getNotificationStyle(totHeight, index, data, this.destroyAnimation);

    // Make sure the description is cut to the set length
    if (desc?.length > this.props.controller.maxDesc) {
      desc = desc.slice(0, this.props.controller.maxDesc) + "...";
    }

    if (this.animate) {
      className += " animate";
    }

    return (
      <div className={"notificationsContainer" + className} id={`card-${index}`} style={style}>
        <div className="notification" ref={(ref) => (this.cardRef = ref)}>
          <div className="header">
            <div className="icon">{this.getIcon(data.type)}</div>
            <span>{data.message}</span>
            <div className="closeBtn" onClick={() => this.handleClose()}>
              {awesomeIcon("times")}
            </div>
          </div>

          {desc && <div className="body">{desc}</div>}
        </div>
      </div>
    );
  }
}
